// Kenya National ID: 7–8 digits
export const isValidKenyaId = (value) => /^\d{7,8}$/.test(value.trim());

// Kenya Passport: 1–2 letters followed by 6–9 digits (e.g. A1234567, AB1234567)
export const isValidKenyaPassport = (value) => /^[A-Za-z]{1,2}\d{6,9}$/.test(value.trim());

// Accepts either a valid National ID or Passport
export const isValidKenyaIdOrPassport = (value) =>
  isValidKenyaId(value) || isValidKenyaPassport(value);

// Kenya company registration numbers.
//
// This used to enumerate four exact patterns, and rejected a great many real
// numbers as a result: modern BRS/eCitizen numbers are alphanumeric after the
// prefix (PVT-9LKJ4G), certificates print spaces around the slashes
// (CPR / 2022 / 123456), older numbers can be four digits or twelve, and some
// carry a letter suffix or a trailing year. A supplier holding any of those was
// simply unable to get past the form.
//
// Guessing a national registry's numbering scheme from the outside is a losing
// game, and being wrong blocks legitimate onboarding — the expensive direction
// of the trade-off. So this now only rejects input that is obviously not a
// registration number at all, and leaves the judgement to the procurement
// reviewer who sees the certificate. KENYA_REG_HELPER is guidance, not a spec.
export const isValidKenyaCompanyReg = (value) => {
  const v = String(value ?? '').trim();

  // Long enough to be meaningful, short enough to be a number and not prose.
  if (v.length < 4 || v.length > 30) return false;

  // A digit was required here on the assumption every format has one. Wrong:
  // modern BRS registrations issue an opaque suffix after the prefix
  // (PVT-AJUXEVD) that can land on an all-letter draw with no digit at all —
  // rejected outright, blocking a real supplier holding one. The structural
  // marker a registrar-issued code actually has is a separator (a prefix, then
  // / . or -), which plain free text like "pending" or "N/A" (once past the
  // length check) does not. A hyphen alone, without letters+digits either side,
  // is not enough — "----" would slip through digit-or-separator with nothing
  // else to anchor it.
  const hasDigit = /\d/.test(v);
  const hasSeparator = /[./-]/.test(v);
  if (!hasDigit && !hasSeparator) return false;

  // Letters, digits, and the separators that actually appear on certificates.
  // Must start and end with a letter or digit.
  return /^[A-Za-z0-9][A-Za-z0-9./\- ]*[A-Za-z0-9]$/.test(v);
};

// Standard email format
export const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const KENYA_ID_HELPER = 'National ID: 7–8 digits. Passport: 1–2 letters followed by 6–9 digits (e.g. A1234567)';
export const KENYA_REG_HELPER = 'Enter it exactly as printed on your certificate — e.g. CPR/2022/123456, PVT-9LKJ4G, or 300693';
export const EMAIL_HELPER = 'Enter a valid email address (e.g. name@example.com)';
